import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/hooks/useApi';
import type { FolderPurpose } from '@/lib/folderPurpose';
import type {
  DiscoveryProspect,
  DiscoveryProspectStatus,
  FolderCampaign,
  InviteChannel,
} from '@/api/types';

/**
 * Admin-side view of the discovery queue.
 *
 * Distinct from `useDiscoveryQueue`, which is the *outreacher's* view and is
 * hard-scoped to what one inviter is allowed to claim. Admins need the whole
 * queue, including claimed, dismissed and promoted rows.
 */

export interface AdminProspect extends DiscoveryProspect {
  /** Joined server-side so the table can name who is sitting on a lead. */
  assignedToInviterName: string | null;
  /**
   * Every outreacher who marked this lead irrelevant to its folder, with the
   * note they left. Admin-only: the outreacher list carries only the caller's
   * own mark, because one person's opinion must not clear a teammate's queue.
   */
  flaggedBy: { inviterName: string; note: string | null }[];
  flagCount: number;
  /**
   * Most recent DM anyone sent this creator. The backend computes it for the
   * contact summary but doesn't put it on admin rows yet; the Lead queue
   * shows "Since last message" only when it's present.
   */
  lastContactedAt?: string | null;
}

export interface AdminProspectFilters {
  status?: DiscoveryProspectStatus;
  /** Comma list of statuses, e.g. `AVAILABLE,CLAIMED` for "not messaged yet". */
  statuses?: string;
  channel?: InviteChannel;
  folderPublicId?: string;
  /** Only leads from folders tagged for this purpose (untagged = clipping). */
  folderPurpose?: FolderPurpose;
  assignedToTeam?: string;
  /** Comma list: leave out leads assigned to these teams or their outreachers (main view: Influencer, Vyral). */
  excludeTeam?: string;
  search?: string;
  minAgeDays?: number;
  /** `only` = leads someone called irrelevant; `none` = everything else. */
  flagged?: 'only' | 'none';
  page?: number;
  limit?: number;
}

export interface AdminFolderSummary {
  sourceFolderPublicId: string | null;
  sourceFolderName: string | null;
  /** What the folder is for. Optional: an older backend doesn't send it (read as clipping). */
  purpose?: FolderPurpose;
  available: number;
  claimed: number;
  promoted: number;
  dismissed: number;
  total: number;
  lastExportedAt: string;
  oldestUnclaimedDays: number | null;
  /**
   * Not-messaged-yet leads (Open + Claimed) at least one outreacher marked
   * irrelevant. Optional: an older backend doesn't send it, and missing
   * means unknown — never zero.
   */
  flaggedUnmessaged?: number | null;
  /** Raw stored links, including any that no longer resolve. */
  campaignIds: string[];
  /** The subset that resolved to a live campaign — what the chips show. */
  campaigns: FolderCampaign[];
}

const QUEUE_KEY = ['admin', 'prospects'] as const;

export function useAdminProspectFolders() {
  const api = useApi();
  return useQuery({
    queryKey: [...QUEUE_KEY, 'folders'],
    queryFn: () => api.get<AdminFolderSummary[]>('/admin/prospects/folders'),
    staleTime: 15_000,
  });
}

/**
 * Live clipping campaigns an admin can link a folder to.
 *
 * `available: false` means clipping didn't answer — distinct from an empty
 * list, which is a normal week with nothing running. Cached for a while: the
 * catalogue changes on a human timescale and every folder card reads it.
 */
export function useSelectableCampaigns() {
  const api = useApi();
  return useQuery({
    queryKey: [...QUEUE_KEY, 'selectable-campaigns'],
    queryFn: () =>
      api.get<{ campaigns: FolderCampaign[]; available: boolean }>('/admin/prospects/campaigns'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Link/unlink a folder's campaigns from the queue overview.
 *
 * Sends only `campaignIds`; the upsert touches just the fields supplied, so
 * this can't wipe the folder's pitch notes or free-text campaign note.
 */
export function useSaveFolderCampaigns() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      folderPublicId,
      folderName,
      campaignIds,
    }: {
      folderPublicId: string;
      folderName: string | null;
      campaignIds: string[];
    }) =>
      api.put<unknown>(`/admin/prospects/folder-guidance/${encodeURIComponent(folderPublicId)}`, {
        // Sent so a folder that had no guidance row yet still gets a readable
        // name on it, matching what the discovery-side editor writes.
        sourceFolderName: folderName,
        campaignIds,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...QUEUE_KEY, 'folders'] }),
  });
}

export function useAdminProspects(filters: AdminProspectFilters) {
  const api = useApi();
  return useQuery({
    queryKey: [...QUEUE_KEY, 'list', filters],
    queryFn: () =>
      api.getPaged<AdminProspect[]>('/admin/prospects', {
        ...filters,
      } as Record<string, string | number | boolean | undefined>),
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  });
}

function useQueueInvalidator() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: QUEUE_KEY });
}

export function useDismissProspects() {
  const api = useApi();
  const invalidate = useQueueInvalidator();
  return useMutation({
    mutationFn: ({ publicIds, reason }: { publicIds: string[]; reason?: string }) =>
      api.post<{ dismissed: string[]; skipped: string[] }>('/admin/prospects/bulk-dismiss', {
        publicIds,
        reason: reason ?? null,
      }),
    onSuccess: invalidate,
  });
}

/**
 * Close a folder: remove every lead in it nobody has messaged yet (Open and
 * Claimed). Messaged and logged leads stay, and removed ones can be restored.
 */
export function useCloseFolder() {
  const api = useApi();
  const invalidate = useQueueInvalidator();
  return useMutation({
    mutationFn: ({
      folderPublicId,
      reason,
      statuses,
    }: {
      folderPublicId: string;
      reason?: string;
      /** Which leads to remove. The server defaults to the unmessaged ones (Open + Claimed) and never touches logged leads. */
      statuses?: DiscoveryProspectStatus[];
    }) =>
      api.post<{ dismissed: number }>('/admin/prospects/dismiss-by-folder', {
        folderPublicId,
        reason: reason ?? null,
        ...(statuses ? { statuses } : {}),
      }),
    onSuccess: invalidate,
  });
}

export function useUndismissProspect() {
  const api = useApi();
  const invalidate = useQueueInvalidator();
  return useMutation({
    mutationFn: (publicId: string) =>
      api.post<AdminProspect>(`/admin/prospects/${encodeURIComponent(publicId)}/undismiss`),
    onSuccess: invalidate,
  });
}

export function useDeleteProspects() {
  const api = useApi();
  const invalidate = useQueueInvalidator();
  return useMutation({
    mutationFn: (publicIds: string[]) =>
      api.post<{ deleted: string[]; skipped: string[] }>('/admin/prospects/bulk-delete', {
        publicIds,
      }),
    onSuccess: invalidate,
  });
}

export function useReassignProspects() {
  const api = useApi();
  const invalidate = useQueueInvalidator();
  return useMutation({
    mutationFn: (input: {
      publicIds: string[];
      assignedToInviterId?: number | null;
      assignedToTeam?: string | null;
    }) => {
      return reassignInBatches(input, (batch) =>
        api.post<{ reassigned: string[] }>('/admin/prospects/reassign', batch));
    },
    onSuccess: invalidate,
  });
}

// ── Folder guidance (the briefing outreachers see) ────────────────────────

export interface FolderGuidance {
  sourceFolderPublicId: string;
  sourceFolderName: string | null;
  recommendedCampaigns: string | null;
  campaignIds: string[] | null;
  pitchNotes: string | null;
  updatedByName?: string | null;
  updatedAt?: string | null;
}

/** One folder's stored briefing; null when nobody has written one yet. */
export function useFolderGuidance(folderPublicId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: [...QUEUE_KEY, 'guidance', folderPublicId],
    queryFn: () =>
      api.get<FolderGuidance | null>(
        `/admin/prospects/folder-guidance/${encodeURIComponent(folderPublicId ?? '')}`,
      ),
    enabled: !!folderPublicId,
  });
}

/**
 * Save a folder's briefing. Only the fields supplied are written (the upsert
 * is partial), so editing the pitch can't drop campaign links and vice versa.
 */
export function useSaveFolderGuidance() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      folderPublicId,
      ...body
    }: {
      folderPublicId: string;
      sourceFolderName: string | null;
      recommendedCampaigns?: string | null;
      pitchNotes?: string | null;
      campaignIds?: string[];
    }) => api.put<FolderGuidance>(`/admin/prospects/folder-guidance/${encodeURIComponent(folderPublicId)}`, body),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: [...QUEUE_KEY, 'folders'] });
      void qc.invalidateQueries({ queryKey: [...QUEUE_KEY, 'guidance', vars.folderPublicId] });
      // Outreachers' briefings read the same row.
      void qc.invalidateQueries({ queryKey: ['portal', 'discovery-folders'] });
    },
  });
}

// ── Folder table helpers (tested in src/lib/__tests__/queue-copy.test.ts) ─

/**
 * Leads someone messaged but nobody has logged. The admin summary doesn't
 * count CONTACTED on its own, so it's what's left of the total after the
 * statuses it does count. Clamped so an inconsistent summary can't go negative.
 */
export function folderMessagedCount(
  f: Pick<AdminFolderSummary, 'total' | 'available' | 'claimed' | 'promoted' | 'dismissed'>,
): number {
  return Math.max(0, f.total - f.available - f.claimed - f.promoted - f.dismissed);
}

export type FolderSort = 'attention' | 'oldest' | 'newest' | 'progress';

/** Leads in a folder nobody has messaged yet — what closing a folder removes. */
export function folderUnmessagedCount(f: Pick<AdminFolderSummary, 'available' | 'claimed'>): number {
  return f.available + f.claimed;
}

/**
 * Leads closing a folder can still remove: unmessaged plus messaged-waiting.
 * Zero means the folder is finished — every lead is logged or removed.
 */
export function folderRemovableCount(
  f: Pick<AdminFolderSummary, 'total' | 'available' | 'claimed' | 'promoted' | 'dismissed'>,
): number {
  return folderUnmessagedCount(f) + folderMessagedCount(f);
}

/** A folder with at least this share worked and something left is "nearly done". */
export const NEARLY_DONE_PERCENT = 90;
/** Leftovers at least this share flagged irrelevant read as junk. */
export const JUNK_LEFTOVER_PERCENT = 50;

type ProgressInput = Pick<
  AdminFolderSummary,
  'total' | 'available' | 'claimed' | 'promoted' | 'dismissed' | 'oldestUnclaimedDays'
> & { flaggedUnmessaged?: number | null };

export interface FolderProgress {
  /** Messaged + logged + removed: every lead someone has dealt with. */
  worked: number;
  /** Not messaged yet (Open + Claimed). */
  left: number;
  total: number;
  /** Whole percent worked, rounded down so 89.9% never reads as 90%; null for an empty folder. */
  percent: number | null;
  /** Leftovers marked irrelevant; null when the server didn't say (unknown, not zero). */
  flaggedLeft: number | null;
  nearlyDone: boolean;
}

/**
 * How far through a folder the team is. "Worked" is anything someone has
 * dealt with — messaged, logged or removed — so the share left is exactly
 * what closing the folder would remove.
 */
export function folderProgress(f: ProgressInput): FolderProgress {
  const total = Math.max(0, f.total);
  const left = Math.min(total, Math.max(0, folderUnmessagedCount(f)));
  const worked = Math.min(total - left, folderMessagedCount(f) + Math.max(0, f.promoted) + Math.max(0, f.dismissed));
  const flagged = typeof f.flaggedUnmessaged === 'number' ? Math.min(left, Math.max(0, f.flaggedUnmessaged)) : null;
  return {
    worked,
    left,
    total,
    percent: total > 0 ? Math.floor((worked * 100) / total) : null,
    flaggedLeft: flagged,
    // Integer comparison: no float edge at exactly 90%.
    nearlyDone: total > 0 && left > 0 && worked * 100 >= NEARLY_DONE_PERCENT * total,
  };
}

/**
 * What a nearly-done folder's leftovers look like, so an admin closing it
 * knows whether they're throwing away junk or good leads nobody reached:
 *
 *   junk:   ≥ half the leftovers are marked irrelevant.
 *   fresh:  few marked, and none has sat untouched `staleDays`+ — likely
 *           just not reached yet.
 *   passed: few marked, but untouched `staleDays`+ — people scrolled past
 *           without saying why.
 *
 * null when there's nothing left, or the flag count is unknown.
 */
export type LeftoverVerdict = 'junk' | 'fresh' | 'passed';

export function leftoverVerdict(f: ProgressInput, staleDays: number): LeftoverVerdict | null {
  const p = folderProgress(f);
  if (p.left === 0 || p.flaggedLeft === null) return null;
  if (p.flaggedLeft * 100 >= JUNK_LEFTOVER_PERCENT * p.left) return 'junk';
  const age = f.available > 0 ? f.oldestUnclaimedDays : null;
  return age !== null && age >= staleDays ? 'passed' : 'fresh';
}

export function leftoverVerdictText(verdict: LeftoverVerdict, staleDays: number): string {
  if (verdict === 'junk') return 'Leftovers look like junk — safe to close';
  if (verdict === 'fresh') return 'Leftovers may still be good — worth a look';
  return `Leftovers untouched ${staleDays}+ days, few flagged — skim, then close`;
}

/** "3 left — 2 marked irrelevant · oldest untouched 9d"; unknown parts are left out. */
export function leftoverBreakdown(f: ProgressInput): string {
  const p = folderProgress(f);
  if (p.left === 0) return 'Nothing left to message';
  const parts = [
    p.flaggedLeft !== null ? `${p.flaggedLeft} marked irrelevant` : null,
    f.available > 0 && f.oldestUnclaimedDays !== null ? `oldest untouched ${Math.max(0, f.oldestUnclaimedDays)}d` : null,
  ].filter((x): x is string => !!x);
  return `${p.left} left${parts.length > 0 ? ` — ${parts.join(' · ')}` : ''}`;
}

/**
 * Folder table order.
 *
 *   attention: folders with open leads first, stale before busy, then most
 *              open; worked-through folders last.
 *   oldest:    longest-untouched open lead first; folders with nothing open
 *              last.
 *   newest:    most recently released (exported) first.
 *   progress:  nearly-done folders first, then most worked; folders with
 *              nothing left to message last.
 */
export function sortFolders(
  folders: ReadonlyArray<AdminFolderSummary>,
  sort: FolderSort,
  staleDays: number,
): AdminFolderSummary[] {
  const age = (f: AdminFolderSummary): number => (f.available > 0 ? f.oldestUnclaimedDays ?? 0 : -1);
  return [...folders].sort((a, b) => {
    if (sort === 'oldest') return age(b) - age(a) || b.available - a.available;
    if (sort === 'newest') return b.lastExportedAt.localeCompare(a.lastExportedAt);
    if (sort === 'progress') {
      const pa = folderProgress(a);
      const pb = folderProgress(b);
      const rank = (p: FolderProgress): number => (p.nearlyDone ? 2 : p.left > 0 ? 1 : 0);
      return rank(pb) - rank(pa) || (pb.percent ?? -1) - (pa.percent ?? -1) || pa.left - pb.left;
    }
    const aActive = a.available > 0 ? 1 : 0;
    const bActive = b.available > 0 ? 1 : 0;
    if (aActive !== bActive) return bActive - aActive;
    const aStale = age(a) >= staleDays ? 1 : 0;
    const bStale = age(b) >= staleDays ? 1 : 0;
    if (aStale !== bStale) return bStale - aStale;
    if (aStale && bStale) return age(b) - age(a);
    return b.available - a.available || folderMessagedCount(b) - folderMessagedCount(a);
  });
}

/** Respect the API's 500-lead limit, including selections across every page. */
export async function reassignInBatches(
  input: { publicIds: string[]; assignedToInviterId?: number | null; assignedToTeam?: string | null },
  send: (batch: typeof input) => Promise<{ reassigned: string[] }>,
): Promise<{ reassigned: string[] }> {
  const reassigned: string[] = [];
  for (let offset = 0; offset < input.publicIds.length; offset += 500) {
    const result = await send({ ...input, publicIds: input.publicIds.slice(offset, offset + 500) });
    reassigned.push(...result.reassigned);
  }
  return { reassigned };
}
